/**
 * Analytic event helper functions
 *
 * @packageDocumentation
 */

import { CUSTOM_EVENT_TYPE } from 'constants/types'

/**
 * Dispatch `form_submission` Analytics Custom Event with `form_name` and optional `input_text` custom event attributes.
 * NOTE:
 *  1. Personal Identifiable Information (PII) should NOT be attached as `input_text` value.
 *  2. Only one input element value can be set as `input_text` optional event attribute from the given form.
 * @param {string} formName Form name
 * @param {HTMLInputElement} targetFormInputElement  HTML input text element
 * @param {boolean} inputElementValueAttached Whether input text element's value need to be attached as event attribute for the custom event or not. PII value should NOT be attached (`false`).
 * @returns {void}
 */
export const dispatchAnalyticsFormSubmissionEvent = (
    formName: string,
    targetFormInputElement: HTMLInputElement,
    inputElementValueAttached: boolean
): void => {
    if (!(targetFormInputElement && targetFormInputElement.value)) return

    const { mparticleInputTextKey, inputTextValue } = getInputTextKeyValue(
        targetFormInputElement,
        inputElementValueAttached
    )

    const detail = {
        event_name: 'form_submission',
        control_fields: {
            mparticle: {
                keys: {
                    form_name: 'form_name',
                    ...mparticleInputTextKey
                },
                mp_event_type: mparticleEventType(formName)
            }
        },
        form_name: formName,
        ...inputTextValue
    }
    //dispatch Analytic Event
    document.dispatchEvent(new CustomEvent(CUSTOM_EVENT_TYPE, { detail }))
}

/**
 * Build mParticle event attribute key with input element value if inputElementValueAttached is true.
 * @param {HTMLInputElement} targetFormInputElement
 * @param {boolean} inputElementValueAttached
 * @returns {Record<string, Record<string, string>>}
 */
const getInputTextKeyValue = (
    targetFormInputElement: HTMLInputElement,
    inputElementValueAttached: boolean
): Record<string, Record<string, string>> => {
    let inputTextValue = {}
    let mparticleInputTextKey = {}
    if (inputElementValueAttached) {
        inputTextValue = { input_text: targetFormInputElement.value }
        mparticleInputTextKey = { input_text: 'input_text' }
    }
    return { mparticleInputTextKey, inputTextValue }
}

/**
 * Get mParticle native event type based on the given form name.
 * `Search` is for search forms. `Other` for rest of the forms.
 * @param {string} formName Form name
 * @returns {string} mParticle Event types `Search` or `Other`
 */
const mparticleEventType = (formName: string): string =>
    formName?.toLowerCase() === 'search' ? 'Search' : 'Other'
